import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MessageCircle } from 'lucide-react';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import SentMessagesFilters from './components/SentMessagesFilters';
import SentMessageDialog from './components/SentMessageDialog';
import SentMessagesCounter from './components/SentMessagesCounter';
import {
  TRACKING_START_DATE,
  messageTypeLabels,
  useSentMessagesScreen,
} from './hooks/useSentMessagesScreen';

export default function SentMessagesWeb() {
  const {
    isLoading,
    error,
    selectedMonth,
    setSelectedMonth,
    messages,
    isBeforeTrackingStart,
    selectedMessage,
    openMessage,
    closeMessage,
    openReference,
  } = useSentMessagesScreen();

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    setShowErrorMessage(Boolean(error));
  }, [error]);

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, bgcolor: 'background.paper' }}>
      <Loading isToHidden={isLoading === false} />

      {showErrorMessage && (
        <ErrorMessage message={error} show={showErrorMessage} setShow={setShowErrorMessage} />
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <MessageCircle size={20} />
          </Box>
          <Typography variant="h6">Mensagens Enviadas</Typography>
        </Stack>
      </Stack>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Mensagens de WhatsApp enviadas automaticamente pelo sistema aos pacientes
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <SentMessagesFilters selectedMonth={selectedMonth} onChangeMonth={setSelectedMonth} />
          <SentMessagesCounter total={messages.length} selectedMonth={selectedMonth} />
        </Stack>

        {isBeforeTrackingStart && (
          <Alert severity="warning" sx={{ mb: 2 }} data-cy="sent-messages-tracking-notice">
            Essa funcionalidade só começou a contar as mensagens a partir de{' '}
            {TRACKING_START_DATE.format('DD/MM/YYYY')}.
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Enviada em</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Destinatário</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Mensagem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma mensagem enviada no mês selecionado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <TableRow
                    key={message.id}
                    hover
                    data-cy="sent-message-row"
                    onClick={() => openMessage(message)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{message.sentAt}</TableCell>
                    <TableCell>
                      <Chip
                        label={messageTypeLabels[message.type] ?? message.type}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{message.recipientName || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {message.recipientPhone || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" noWrap>
                        {message.content}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <SentMessageDialog
        message={selectedMessage}
        onClose={closeMessage}
        onOpenReference={openReference}
      />
    </Paper>
  );
}
